/**
 * Централизованная конфигурация для frontend
 * Все настройки приложения собраны в одном месте
 */

interface ApiConfig {
  baseUrl: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
}

interface AppConfig {
  name: string;
  version: string;
  env: string;
}

interface FeaturesConfig {
  enableNewsletter: boolean;
  enableAuth: boolean;
  enablePayments: boolean;
  enableAnalytics: boolean;
}

interface GoogleAnalyticsConfig {
  measurementId: string;
  enabled: boolean;
}

interface AnalyticsConfig {
  googleAnalytics: GoogleAnalyticsConfig;
}

interface Config {
  api: ApiConfig;
  app: AppConfig;
  features: FeaturesConfig;
  analytics: AnalyticsConfig;
}

// Определяем окружение
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// Простая конфигурация API - всегда используем относительные пути
// Development: прокси в package.json перенаправляет /api/* на localhost:54321
// Production: nginx перенаправляет /api/* на backend
const getApiBaseUrl = (): string => {
  // Всегда используем пустую строку, так как все пути начинаются с /api/
  return '';
};

const config: Config = {
  api: {
    baseUrl: getApiBaseUrl(),
    timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000', 10),
    retryAttempts: parseInt(process.env.REACT_APP_API_RETRY_ATTEMPTS || '3', 10),
    retryDelay: parseInt(process.env.REACT_APP_API_RETRY_DELAY || '1000', 10)
  },
  
  app: {
    name: process.env.REACT_APP_NAME || 'SONIC MAKERS',
    version: process.env.REACT_APP_VERSION || '1.0.0',
    env: process.env.NODE_ENV || 'development'
  },
  
  features: {
    enableNewsletter: process.env.REACT_APP_ENABLE_NEWSLETTER !== 'false',
    enableAuth: process.env.REACT_APP_ENABLE_AUTH !== 'false',
    enablePayments: process.env.REACT_APP_ENABLE_PAYMENTS === 'true',
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true'
  },
  
  analytics: {
    googleAnalytics: {
      measurementId: process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-ZP9LE5HM05',
      enabled: process.env.REACT_APP_ENABLE_ANALYTICS === 'true'
    }
  }
};

// Валидация конфигурации
const validateConfig = () => {
  const errors: string[] = [];
  
  if (config.api.timeout < 1000) {
    errors.push('API timeout should be at least 1000ms');
  }
  
  if (config.api.retryAttempts < 0 || config.api.retryAttempts > 10) {
    errors.push('API retry attempts should be between 0 and 10');
  }
  
  if (errors.length > 0) {
    console.error('❌ Frontend configuration errors:');
    errors.forEach(error => console.error(`   - ${error}`));
    throw new Error('Invalid frontend configuration');
  }
};

// Проверяем конфигурацию
validateConfig();

// Логируем конфигурацию в development
if (isDevelopment) {
  console.log('🎨 Frontend configuration:');
  console.log(`   App: ${config.app.name} v${config.app.version}`);
  console.log(`   Environment: ${config.app.env}`);
  console.log(`   API: Using proxy for /api/* requests`);
  console.log(`   Features:`, config.features);
}

export default config;

// Экспортируем отдельные части для удобства
export const { api: apiConfig, app: appConfig, features: featuresConfig } = config; 